/**
 * Inicialización de Firebase.
 *
 * La configuración NO vive aquí: se lee de variables de entorno (`.env.local`, que
 * está en `.gitignore`). La llave de un proyecto Firebase es pública por diseño
 * —viaja en el paquete del navegador— pero mantenerla fuera del repositorio evita
 * que alguien apunte por accidente a producción desde una rama de pruebas.
 *
 * Lo que protege de verdad son las reglas de `firestore.rules`, no esconder la llave.
 */

import { initializeApp } from 'firebase/app'
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
  connectFirestoreEmulator
} from 'firebase/firestore'
import {
  getAuth, connectAuthEmulator, onAuthStateChanged,
  signInWithEmailAndPassword, signOut
} from 'firebase/auth'

const cfg = {
  apiKey: import.meta.env.VITE_FB_API_KEY,
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FB_PROJECT_ID || 'demo-soham-home',
  storageBucket: import.meta.env.VITE_FB_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FB_SENDER_ID,
  appId: import.meta.env.VITE_FB_APP_ID
}

export const app = initializeApp(cfg)

/**
 * Persistencia sin conexión. Es la razón por la que se eligió Firestore sobre las
 * alternativas: el vendedor captura en una casa sin señal y sincroniza al salir.
 * `persistentMultipleTabManager` evita que dos pestañas se peleen la caché.
 */
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  /* LONG-POLLING FORZADO (2026-08-19): el transporte de streaming de Firestore
     (WebChannel) lo estrangulan los filtros web tipo antivirus/VPN — caso real:
     el iPhone de Juan con Kaspersky se quedaba en «Cargando…» para siempre,
     mientras la laptop entraba en segundos. La regla de la casa es que la app
     funcione en el teléfono QUE SEA, con el filtro que traiga, sin pedirle a
     nadie que desactive su seguridad: long-polling son peticiones HTTPS
     normales que atraviesan cualquier proxy, a cambio de unos milisegundos. */
  experimentalForceLongPolling: true
})

export const auth = getAuth(app)

// Con el emulador no se toca la nube: ni cuenta, ni tarjeta, ni datos reales.
if (import.meta.env.VITE_EMULADOR === '1') {
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
}

/* ---------- Sesión ---------- */

let usuarioActual = null
let sesionResuelta = false
const enEspera = []

onAuthStateChanged(auth, u => {
  usuarioActual = u
  sesionResuelta = true
  while (enEspera.length) enEspera.shift()(u)
})

/** Promesa que resuelve cuando hay usuario. Nada se lee antes de esto. */
export function esperarSesion() {
  if (sesionResuelta && usuarioActual) return Promise.resolve(usuarioActual)
  return new Promise(resolve => {
    const reintentar = u => (u ? resolve(u) : enEspera.push(reintentar))
    if (sesionResuelta) reintentar(usuarioActual)
    else enEspera.push(reintentar)
  })
}

export const usuario = () => usuarioActual
export const entrar = (correo, clave) => signInWithEmailAndPassword(auth, correo, clave)
export const salir = () => signOut(auth)
export const observarSesion = cb => onAuthStateChanged(auth, cb)

/**
 * El rol vive en los custom claims del token, no en un documento que el navegador
 * compare. En el sistema anterior bastaba abrir la consola del navegador para
 * saltarse el control de permisos.
 */
export async function rolActual() {
  if (!usuarioActual) return null
  const token = await usuarioActual.getIdTokenResult()
  return token.claims.rol || null
}

/** Todos los claims del token (rol, ramo, equipo). Misma autoridad que rolActual. */
export async function claimsActuales() {
  if (!usuarioActual) return {}
  const token = await usuarioActual.getIdTokenResult()
  return token.claims || {}
}

/* El contador de escrituras en vuelo que vivía aquí se retiró en E0 (2026-08-22):
   valía 0 al reabrir la app con escrituras encoladas de la sesión anterior, así
   que decía «todo sincronizado» justo cuando no lo estaba. El estado real lo da
   `waitForPendingWrites` en `datos/firestore.js`. */
